import React from "react";
import { MessageCircle } from "lucide-react";
import { getIcon, whatsappLink } from "@/data/catalogHelpers";
import { trackClick } from "@/lib/api";
import ScrollableArea from "./ScrollableArea";

const isVideoUrl = (url) => /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url || "");

export default function ModalityPage({ studio, modality, pageNumber, totalPages }) {
  const m = modality || {};
  const s = studio || {};
  const Icon = getIcon(m.icon);
  const compact = m.compact || (m.procedures && m.procedures.length >= 6);
  const featured = m.featured || (m.procedures && m.procedures.length === 1);
  const consult = m.consultOnly;
  const hasImage = Boolean(m.image);
  const isVideo = hasImage && isVideoUrl(m.image);

  const autoHeight = consult ? 38 : featured ? 44 : compact ? 25 : 33;
  const customHeight = Number(m.imageHeightPct) > 0 ? Number(m.imageHeightPct) : 0;
  const imageHeight = `${customHeight || autoHeight}%`;

  const mediaStyle = {
    objectFit: m.imageFit === "contain" ? "contain" : "cover",
    objectPosition: `${m.imageFocusX ?? 50}% ${m.imageFocusY ?? 50}%`,
    transform: `scale(${(m.imageZoom ?? 100) / 100})`,
    transformOrigin: `${m.imageFocusX ?? 50}% ${m.imageFocusY ?? 50}%`,
  };

  return (
    <div
      className="relative w-full h-full paper-bg flex flex-col"
      data-testid={`modality-page-${m.slug || pageNumber}`}
    >
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-5 pt-4 pb-2">
        <span className="font-script text-base text-[var(--champagne)]">
          {s.brandName || "Bruna Reis"}
        </span>
        <span className="w-9 h-9 flex items-center justify-center border border-[rgba(212,175,55,0.35)] rounded-full">
          <Icon size={17} className="text-[var(--gold)]" strokeWidth={1.3} />
        </span>
      </div>

      {/* Image (opcional) */}
      {hasImage ? (
        <div className="relative w-full shrink-0 px-5" style={{ height: imageHeight }}>
          <div className="relative w-full h-full overflow-hidden bg-[var(--satin-black)]">
            {isVideo ? (
              <video
                key={m.image}
                src={m.image}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full"
                style={mediaStyle}
              />
            ) : (
              <img
                src={m.image}
                alt={m.title}
                loading="lazy"
                className="w-full h-full"
                style={mediaStyle}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--paper)] via-transparent to-transparent pointer-events-none" />
            <div className="absolute inset-0 border border-[rgba(212,175,55,0.25)] pointer-events-none" />
          </div>
        </div>
      ) : (
        /* Elegant no-image ornament: keeps composition centered and premium */
        <div
          className="relative w-full shrink-0 px-5 flex items-center justify-center"
          data-testid={`modality-page-no-image-${m.slug || pageNumber}`}
          style={{ height: compact ? "14%" : "18%" }}
        >
          <div className="flex items-center gap-4 w-full max-w-[18rem]">
            <span className="flex-1 h-px bg-[rgba(212,175,55,0.35)]" />
            <span className="w-11 h-11 flex items-center justify-center border border-[rgba(212,175,55,0.4)] rounded-full">
              <Icon size={18} className="text-[var(--gold)]" strokeWidth={1.3} />
            </span>
            <span className="flex-1 h-px bg-[rgba(212,175,55,0.35)]" />
          </div>
        </div>
      )}

      {/* Title + phrase */}
      <div className="shrink-0 px-6 pt-3">
        <h2
          className={`font-heading text-[var(--offwhite)] leading-none ${
            compact ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl"
          }`}
        >
          {m.title}
        </h2>
        <p className="font-body italic text-[var(--nude)] text-[0.72rem] sm:text-xs leading-relaxed mt-1.5 tracking-wide whitespace-pre-line">
          {m.phrase}
        </p>
        <div className="gold-hairline w-full mt-3" />
      </div>

      {/* Procedures / consult */}
      <ScrollableArea className="flex-1 min-h-0" contentClassName="px-6 pt-3 pb-8">
        {consult ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <p className="font-heading text-3xl text-gold-metallic">Valores sob consulta</p>
            <p className="font-body text-[var(--muted)] text-[0.7rem] mt-2 max-w-[16rem] tracking-wide">
              Atendimento com avaliação individual para garantir o melhor resultado.
            </p>
          </div>
        ) : featured ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <p className="font-body uppercase tracking-[0.28em] text-[var(--nude)] text-[0.62rem]">
              {m.procedures[0].name}
            </p>
            <p className="font-heading text-5xl text-gold-metallic mt-2">
              {m.procedures[0].price}
            </p>
          </div>
        ) : (
          <ul className={compact ? "space-y-1.5" : "space-y-2.5"}>
            {(m.procedures || []).map((p, i) => (
              <li
                key={i}
                data-testid={`price-row-${m.slug || pageNumber}-${i}`}
                className="flex items-end"
              >
                <span
                  className={`font-body text-[var(--offwhite)] tracking-wide ${
                    compact ? "text-[0.72rem]" : "text-[0.8rem] sm:text-sm"
                  }`}
                >
                  {p.name}
                </span>
                <span className="dotted" />
                <span
                  className={`font-body font-semibold text-[var(--gold)] whitespace-nowrap ${
                    compact ? "text-[0.72rem]" : "text-[0.8rem] sm:text-sm"
                  }`}
                >
                  {p.price}
                </span>
              </li>
            ))}
          </ul>
        )}
      </ScrollableArea>

      {/* Action */}
      <div className="shrink-0 px-6 pb-1.5 pt-2">
        <a
          href={whatsappLink(m.title, consult)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackClick(m.id)}
          data-testid={`schedule-button-${m.slug || pageNumber}`}
          className="flex items-center justify-center gap-2 w-full py-2.5 border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--deep-black)] transition-colors duration-300 uppercase tracking-[0.16em] text-[0.62rem] font-body font-semibold"
        >
          <MessageCircle size={14} strokeWidth={1.6} />
          {consult ? "Consultar valores no WhatsApp" : "Agendar este procedimento"}
        </a>
      </div>

      {/* Footer */}
      <div className="shrink-0 flex items-center justify-between px-6 pb-3 pt-1">
        <span className="font-body text-[0.55rem] tracking-[0.2em] text-[var(--muted)]">
          {String(pageNumber).padStart(2, "0")} / {String(totalPages).padStart(2, "0")}
        </span>
        <span className="font-body text-[0.5rem] uppercase tracking-[0.24em] text-[var(--muted)]">
          Studio da Beleza Bruna Reis
        </span>
      </div>
    </div>
  );
}
